import { useNavigate, useLocation } from 'react-router-dom';
import { useCallback } from 'react';
import { JcAppRoute } from './JcAppRoute';
import { JcAppRoutingConfig, RouteProps, RouteState } from './JcAppRoutingConfig';

/**
 * Options for a single navigation call.
 * Use `replace: true` to swap the current history entry instead of pushing a new one.
 * This is the standard pattern for post-auth redirects, post-form-submit redirects,
 * and any "fixing up" navigation the user shouldn't be able to go back to.
 */
export interface JcAppNavigateOptions<StateT = undefined> {
    readonly replace?: boolean;
    /**
     * State attached to the history entry, delivered to the destination page as
     * its `state` prop. Use it to carry a fast-path hint (e.g. an id already in
     * hand at the link site) so the destination can skip a lookup. Dropped on
     * refresh / deep-link / open-in-new-tab, so always keep a path-derived
     * fallback.
     */
    readonly state?: StateT;
}

/**
 * Options for a `navigateBack` call. `fallbackTo` is invoked when there is no
 * history entry to return to (e.g. the user opened the page via a direct link).
 */
export interface JcAppNavigateBackOptions {
    readonly fallbackTo?: () => void;
}

export class JcAppNavigation {
    /**
     * Generates a path for a given route key and props
     */
    static generatePath<RoutesT extends Record<string, JcAppRoute<any, any>>, K extends keyof RoutesT>(
        config: JcAppRoutingConfig<RoutesT>,
        routeKey: K,
        props: RouteProps<RoutesT[K]>
    ): string {
        const route = config.routes[routeKey];
        if (!route) {
            throw new Error(`Route with key "${String(routeKey)}" not found in routing configuration`);
        }
        return route.pathFromProps(props);
    }
}

export interface JcAppNavigationHook<RoutesT extends Record<string, JcAppRoute<any, any>>> {
    readonly navigateToRoute: <K extends keyof RoutesT>(
        routeKey: K,
        props: RouteProps<RoutesT[K]>,
        options?: JcAppNavigateOptions<RouteState<RoutesT[K]>>
    ) => void;
    /**
     * Navigate to an arbitrary URL path. Use this only for opaque paths captured
     * at runtime (e.g. a `returnTo` value); prefer `navigateToRoute` for any
     * statically-known destination so navigation stays type-safe.
     */
    readonly navigateToPath: (path: string, options?: JcAppNavigateOptions<unknown>) => void;
    /**
     * Navigate one entry back in browser history, or invoke `fallbackTo` (if
     * provided) when there's no history to return to.
     */
    readonly navigateBack: (options?: JcAppNavigateBackOptions) => void;
}

export function useJcAppNavigation<RoutesT extends Record<string, JcAppRoute<any, any>>>(
    config: JcAppRoutingConfig<RoutesT>
): JcAppNavigationHook<RoutesT> {
    const navigate = useNavigate();

    const navigateToRoute = useCallback(<K extends keyof RoutesT>(
        routeKey: K,
        props: RouteProps<RoutesT[K]>,
        options?: JcAppNavigateOptions<RouteState<RoutesT[K]>>
    ) => {
        const path = JcAppNavigation.generatePath(config, routeKey, props);
        navigate(path, { replace: options?.replace, state: options?.state });
    }, [config, navigate]);

    const navigateToPath = useCallback((path: string, options?: JcAppNavigateOptions<unknown>) => {
        navigate(path, { replace: options?.replace, state: options?.state });
    }, [navigate]);

    const navigateBack = useCallback((options?: JcAppNavigateBackOptions) => {
        // React Router resets history.state.idx to 0 on the entry where this
        // page load started, so idx > 0 means there's an in-app entry to pop
        // back to (unlike window.history.length, which also counts pages
        // visited before the app ever loaded).
        const historyState = window.history.state as { idx?: number } | null;
        if ((historyState?.idx ?? 0) > 0) {
            navigate(-1);
        } else if (options?.fallbackTo) {
            options.fallbackTo();
        }
    }, [navigate]);

    return {
        navigateToRoute,
        navigateToPath,
        navigateBack,
    };
}

/**
 * Returns the current URL path including query string. Use this when you need
 * to capture the user's current location (e.g. to build a `returnTo` value for
 * an auth redirect) without reaching into react-router directly.
 */
export function useCurrentPath(): string {
    const location = useLocation();
    return location.pathname + location.search;
}
